// MapTimeline.jsx — Ticket 01 (map-editor-modal-ux-audit): DTG timeline UI.
// Ticket 02 (map-timeline-range-rework): DTG start/end now editable via two flanking
// `DatePicker`s instead of a fixed demo window.
//
// Bottom-docked timeline control, modeled on NASA Worldview's `#timeline`: drag the MUI Slider
// to scrub, click directly on the track to jump (native Slider behavior), or use
// ArrowLeft/ArrowRight to step the DTG scrub position one day at a time — all unchanged from
// ticket 01, now just bound to the editable `[start, end]` range from `useDtgRange` instead of a
// fixed demo window. The two `DatePicker`s (date-only — no hours/minutes) let the user move
// `start`/`end` directly; moving either mutually clamps the other so `start <= end` always holds
// (see useDtgRange.js).
//
// This component is a thin, presentational consumer of a `useDtgRange()`-shaped `range` prop;
// MapEditorModalInner owns the hook call so other integrations (satellite imagery, movement
// trail, layers-panel filtering) can read/drive the same range state without re-deriving
// timeline internals.
//
// Animate/play-through-time is explicitly out of scope for this component.

import React, { useCallback } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Slider from '@mui/material/Slider';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { usePickerContext } from '@mui/x-date-pickers/hooks';
import CalendarMonthRounded from '@mui/icons-material/CalendarMonthRounded';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { MAP_CONTROL_TRIGGER_SIZE, MAP_CONTROL_COLUMN_INSET } from '../mapViewConstants';

dayjs.extend(utc);

const MS_PER_DAY = 24 * 60 * 60 * 1000;
const DATE_TRIGGER_MIN_WIDTH = 150;

// Ticket 01 (map-editor-modal-ux-audit): horizontal clearance reserved on the timeline's right
// edge for the floating control column in MapEditorModal.jsx (BasemapControl/CoordGridControl/
// MovementTrailControl — collapsed to a single icon trigger by default, see FloatingControlShell
// there). Derived from that column's own shared layout constants (its right inset + its
// collapsed trigger's footprint) plus a small gutter, rather than the previous hand-tuned
// `right: 100` — so the two files can't silently drift out of sync, and this clearance shrinks
// to match the column's now-much-smaller collapsed-by-default footprint.
const TIMELINE_RIGHT_CLEARANCE = MAP_CONTROL_COLUMN_INSET + MAP_CONTROL_TRIGGER_SIZE + MAP_CONTROL_COLUMN_INSET;

// e.g. "14 MAR 2026" — human-readable, NASA-Worldview-style DTG display.
//
// Bug fix (timezone-conversion day-off-by-one report): must always render `value`'s UTC
// calendar day, never the browser/Node process's local one. This is called with two different
// shapes of `value`: `range.start`/`range.end` (already a UTC-mode dayjs instance from
// useDtgRange — `dayjs(utcInstance)` preserves that mode, so `.utc()` here is a no-op for that
// case) AND the Slider's raw numeric `valueMs` (via `valueLabelFormat={formatDtg}` below) — for
// a plain epoch-ms number, `dayjs(value)` without an explicit `.utc()` parses in LOCAL time,
// which silently shows the wrong calendar day for any DTG landing on a UTC-midnight boundary
// (every DTG in this date-only timeline does) whenever the local timezone is behind UTC.
export function formatDtg(value) {
    return dayjs(value).utc().format('DD MMM YYYY').toUpperCase();
}

// Ticket (map-timeline-range-rework, button-trigger follow-up): custom `field` slot for the
// start/end `DatePicker`s — renders a button (formatted DTG label + calendar icon) instead of
// MUI's default sectioned text-field input. Uses the v8 `usePickerContext()` custom-field API
// (see https://mui.com/x/react-date-pickers/custom-field/#using-a-button): `triggerRef` anchors
// the calendar popup to this button, `value`/`label` come from the picker's own state (so this
// stays in sync with `range.start`/`range.end` without any extra prop plumbing), and `setOpen`
// toggles the popup open/closed on click. The picker's `value`/`onChange` wiring (bound to
// `range.start`/`range.setStart` and `range.end`/`range.setEnd` in the DatePicker props below) is
// untouched — this only swaps what triggers the popup.
function DtgTriggerButton({ 'data-testid': dataTestId }) {
    const { setOpen, value, label, disabled, triggerRef } = usePickerContext();
    const formatted = value ? formatDtg(value) : null;

    return (
        <Button
            ref={triggerRef}
            data-testid={dataTestId}
            variant="outlined"
            size="small"
            color="inherit"
            disabled={disabled}
            startIcon={<CalendarMonthRounded fontSize="small" />}
            onClick={() => setOpen((prev) => !prev)}
            aria-label={label ? `${label}${formatted ? `: ${formatted}` : ''}` : undefined}
            sx={{ justifyContent: 'flex-start' }}
        >
            {formatted ?? (label ?? 'Select date')}
        </Button>
    );
}

const START_DATE_PICKER_SLOT_PROPS = { field: { 'data-testid': 'dtg-start-trigger' } };
const END_DATE_PICKER_SLOT_PROPS = { field: { 'data-testid': 'dtg-end-trigger' } };
const DATE_PICKER_SLOTS = { field: DtgTriggerButton };

// rerender-memo: parent (MapEditorModalInner) re-renders on every DTG scrub / vertex placed —
// memoized like ContextPanel/MapDrawToolbar. `range` (from useDtgRange, ticket 05) is now a
// memoized stable object when nothing in it changed.
const MapTimeline = React.memo(function MapTimeline({ range }) {
    const { valueMs, startMs, endMs, start, end, setValue, stepDay, setStart, setEnd } = range;

    const handleSliderChange = useCallback((_e, next) => {
        setValue(Array.isArray(next) ? next[0] : next);
    }, [setValue]);

    const handleStartChange = useCallback((next) => {
        if (next && next.isValid()) setStart(next);
    }, [setStart]);

    const handleEndChange = useCallback((next) => {
        if (next && next.isValid()) setEnd(next);
    }, [setEnd]);

    // Own ArrowLeft/ArrowRight for the whole timeline — slider thumb, label, or container,
    // wherever focus lands — via a capture-phase handler. Capturing (rather than a plain bubble
    // handler) lets us preventDefault + stopPropagation *before* the MUI Slider's own built-in
    // arrow-key stepping runs, so a single keypress never double-steps the scrub position.
    // Scoped entirely to this component; does not touch/compete with the global
    // useEditorKeyboardShortcuts hook used elsewhere in MapEditorModal.jsx.
    //
    // Ticket 02/button-trigger follow-up: the two DatePickers' calendar popups use
    // ArrowLeft/ArrowRight themselves (to move focus between days) once opened. Because MUI
    // pickers render their popup as a React child of the DatePicker — even though it's DOM-portaled
    // elsewhere — a keydown inside the popup still reaches this capture-phase handler, so it must
    // no-op for those. The old sectioned text-field inputs signaled this via `role="spinbutton"`;
    // the button-trigger field has no such element, but the open popup's outermost element carries
    // `role="dialog"` (desktop) or `role="tooltip"` (mobile) — see MUI's `viewContainerRole` — so
    // that's what's checked instead.
    const handleKeyDownCapture = useCallback((e) => {
        if (e.target.closest?.('[role="dialog"], [role="tooltip"]')) return;
        if (e.key === 'ArrowLeft') {
            e.preventDefault();
            e.stopPropagation();
            stepDay(-1);
        } else if (e.key === 'ArrowRight') {
            e.preventDefault();
            e.stopPropagation();
            stepDay(1);
        }
    }, [stepDay]);

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Box
                data-testid="map-timeline"
                onKeyDownCapture={handleKeyDownCapture}
                sx={(theme) => ({
                    position: 'absolute', bottom: 12, left: 12, right: TIMELINE_RIGHT_CLEARANCE, zIndex: 3,
                    bgcolor: 'background.paper', borderRadius: 1,
                    boxShadow: theme.shadows[2], border: `1px solid ${theme.palette.divider}`,
                    display: 'flex', alignItems: 'center', gap: 1.5,
                    px: 1.5, py: 0.75,
                })}
            >
                <DatePicker
                    label="DTG start"
                    value={start}
                    onChange={handleStartChange}
                    slots={DATE_PICKER_SLOTS}
                    slotProps={START_DATE_PICKER_SLOT_PROPS}
                />
                <Slider
                    aria-label="DTG cursor"
                    size="small"
                    value={valueMs}
                    min={startMs}
                    max={endMs}
                    step={MS_PER_DAY}
                    onChange={handleSliderChange}
                    valueLabelDisplay="on"
                    valueLabelFormat={formatDtg}
                    sx={{ flex: 1 }}
                />
                <DatePicker
                    label="DTG end"
                    value={end}
                    onChange={handleEndChange}
                    slots={DATE_PICKER_SLOTS}
                    slotProps={END_DATE_PICKER_SLOT_PROPS}
                />
            </Box>
        </LocalizationProvider>
    );
});

export default MapTimeline;
